import { numberToCurrencyFormatter, numFormatterWithSuffix } from './helper';

export interface IClaimMetricsTableData {
  category: string;
  planImpact: string;
  totalPlanPaid: number;
}

export interface IClaimsImpactDataItem {
  id: string;
  parent: string;
  label: string;
  value: string;
  color: string;
}

export interface IClaimsImpactData {
  medicalData: IClaimsImpactDataItem[];
  rxData: IClaimsImpactDataItem[];
}

export interface IHighestPaidDataItem {
  name: string;
  value: string;
}

export interface IHighestPaidData {
  medical: IHighestPaidDataItem;
  rx: IHighestPaidDataItem;
}

export interface ClaimsImpactDataResponse {
  medical_total_charge: number;
  rx_total_charge: number;
  medical_member_paid: number;
  rx_member_paid: number;
  medical_plan_paid: number;
  rx_plan_paid: number;
  medical_deductible: number;
  rx_deductible: number;
  medical_copay: number;
  rx_copay: number;
  medical_coinsurance: number;
  rx_coinsurance: number;
}

export const formatClaimMetricsTableData = (data: IClaimMetricsTableData[]) => {
  const formattedData: any[] = data
    ?.sort((a, b) => {
      return b?.totalPlanPaid - a?.totalPlanPaid;
    })
    .map((item: IClaimMetricsTableData) => {
      if (
        !item?.totalPlanPaid ||
        item?.totalPlanPaid === 0 ||
        isNaN(item?.totalPlanPaid)
      ) {
        return null;
      }
      return [
        item?.category,
        item?.planImpact,
        isNaN(item?.totalPlanPaid)
          ? ''
          : numberToCurrencyFormatter(item?.totalPlanPaid, true),
      ];
    })
    .filter(Boolean);
  return formattedData;
};

export const getHighestPaidOfClaimsImpactData = (
  data: ClaimsImpactDataResponse,
): IHighestPaidData => {
  const medicalHighestPaidData =
    data?.medical_member_paid > data?.medical_plan_paid
      ? {
          name: 'Medical Member Paid',
          value: '$' + numFormatterWithSuffix(data?.medical_member_paid),
        }
      : {
          name: 'Medical Plan Paid',
          value: '$' + numFormatterWithSuffix(data?.medical_plan_paid),
        };

  const rxHighestPaidData =
    data?.rx_member_paid > data?.rx_plan_paid
      ? {
          name: 'Rx Member Paid',
          value: '$' + numFormatterWithSuffix(data?.rx_member_paid),
        }
      : {
          name: 'Rx Plan Paid',
          value: '$' + numFormatterWithSuffix(data?.rx_plan_paid),
        };

  return {
    medical: medicalHighestPaidData,
    rx: rxHighestPaidData,
  };
};

export const formatClaimsImpactData = (
  data: ClaimsImpactDataResponse,
): IClaimsImpactData => {
  return {
    medicalData: [
      {
        id: 'Flare',
        parent: '',
        label: '',
        value: '',
        color: '#FFFFFF',
      },
      {
        id: 'Medical Plan paid',
        parent: 'Flare',
        label: 'Medical Plan paid',
        value: data?.medical_plan_paid?.toString(),
        color: '#B5BDDF',
      },
      {
        id: 'Medical Member paid',
        parent: 'Flare',
        label: 'Medical Member paid',
        value: data?.medical_member_paid?.toString(),
        color: '#6C7DC0',
      },
      {
        id: 'Medical deductible',
        parent: 'Medical Member paid',
        label: 'Medical deductible',
        value: data?.medical_deductible?.toString(),
        color: '#919DD0',
      },
      {
        id: 'Medical copay',
        parent: 'Medical Member paid',
        label: 'Medical copay',
        value: data?.medical_copay?.toString(),
        color: '#B5BEDF',
      },
      {
        id: 'Medical coinsurance',
        parent: 'Medical Member paid',
        label: 'Medical coinsurance',
        value: data?.medical_coinsurance?.toString(),
        color: '#DADEEF',
      },
    ],
    rxData: [
      {
        id: 'Flare',
        parent: '',
        label: '',
        value: '',
        color: '#FFFFFF',
      },
      {
        id: 'Rx Plan paid',
        parent: 'Flare',
        label: 'Rx Plan paid',
        value: data?.rx_plan_paid?.toString(),
        color: '#FA7C34',
      },
      {
        id: 'Rx Member paid',
        parent: 'Flare',
        label: 'Rx Member paid',
        value: data?.rx_member_paid?.toString(),
        color: '#F5945D',
      },
      {
        id: 'Rx deductible',
        parent: 'Rx Member paid',
        label: 'Rx deductible',
        value: data?.rx_deductible?.toString(),
        color: '#FFB48F',
      },
      {
        id: 'Rx copay',
        parent: 'Rx Member paid',
        label: 'Rx copay',
        value: data?.rx_copay?.toString(),
        color: '#FFC2A5',
      },
      {
        id: 'Rx coinsurance',
        parent: 'Rx Member paid',
        label: 'Rx coinsurance',
        value: data?.rx_coinsurance?.toString(),
        color: '#FFEAE0',
      },
    ],
  };
};
